/**
 * LE PLANNING SUR PAPIER — lot 122, 23/10/2026.
 *
 * Demandé par Mohamed : « on va faire chaque chauffeur sa ligne et tout le
 * monde s'enchaîne ».
 *
 * CE QUE CELA VEUT DIRE, et c'est le cœur de ce fichier :
 *
 *   - UN SEUL TABLEAU. Les chauffeurs en lignes, les jours en colonnes,
 *     exactement comme à l'écran. Pas de page par journée, pas de page par
 *     camion : on imprime le planning, pas des extraits.
 *   - IL S'ENCHAÎNE sur autant de pages qu'il en faut.
 *   - UN CHAUFFEUR N'EST JAMAIS COUPÉ EN DEUX PAGES. C'est la contrainte dure,
 *     posée par Mohamed, et c'est elle qui décide du reste.
 *   - AÉRÉ. Le client garde sa ligne, chaque étape garde la sienne. Rien n'est
 *     replié pour gagner de la place : le document s'enchaîne, la hauteur
 *     n'est plus une contrainte, la lisibilité en est une.
 *
 * CE COMPOSANT NE DEMANDE RIEN À L'API. Il reçoit ce que `VuePlanning` a déjà
 * calculé pour l'écran : les mêmes véhicules, les mêmes cases, la même pile,
 * la même recherche. La feuille ne peut donc pas montrer autre chose que
 * l'écran — c'est voulu. Une seconde lecture du planning aurait pu rendre un
 * autre résultat, et personne n'aurait su lequel était le bon.
 *
 * LA RECHERCHE EST ÉCRITE SUR LE DOCUMENT quand elle est active. Sans cette
 * mention, on imprimerait un planning amputé sans le savoir, et on prendrait
 * l'incomplet pour le complet.
 *
 * ---------------------------------------------------------------------------
 * CE QUI EST MESURÉ, et non estimé — 23/10/2026, sur une maquette de dix
 * véhicules, cinq jours et trente-sept ordres :
 *
 *   place en hauteur sur une page ......................... 718 px
 *   dont l'en-tête, répété sur CHAQUE page ................  48 px
 *   reste pour les chauffeurs ............................. 646 px
 *   le chauffeur le plus chargé de la maquette ............ 287 px
 *
 * Aucune ligne ne s'approche donc de la hauteur d'une page, et la règle « ne
 * jamais couper un chauffeur » ne peut pas se retourner contre elle-même.
 *
 * LE PRIX DE CETTE RÈGLE, mesuré lui aussi : 16 px de blanc en bas de la
 * première page, 64 px en bas de la deuxième. Un chauffeur qui ne tient pas
 * dans la place restante passe entier à la page suivante.
 * ------------------------------------------------------------------------ */

import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { statutDit } from './ot.js';
import {
  colonneDite,
  empechementDe,
  finDeSemaine,
  semaineDite,
  teteDeLigne,
  type Bloc,
  type CartePile,
  type GroupePile,
  type Indisponibilite,
  type LigneBloc,
  type Semaine,
  type VehiculePlanning,
} from './planning.js';

/** Ce qu'il faut pour dessiner la feuille — tout vient déjà de l'écran. */
export interface DonneesPlanningImprime {
  readonly semaine: Semaine;
  readonly vehicules: readonly VehiculePlanning[];
  /** La clé est `vehicule|jour`, comme à l'écran. */
  readonly cases: ReadonlyMap<string, readonly Bloc[]>;
  readonly groupes: readonly GroupePile[];
  readonly indisponibilites: readonly Indisponibilite[];
  /** La recherche en cours. Vide quand il n'y en a pas. */
  readonly recherche: string;
  /** L'instant d'impression, en clair. Passé pour rester vérifiable. */
  readonly imprimeLe: string;
}

/**
 * Les étapes d'un ordre, une par ligne.
 *
 * LES NOMS DE SITE PASSENT À LA LIGNE, ils ne sont pas coupés — voir la règle
 * `.plan-lieu` dans la feuille de style, où la mesure est consignée.
 */
function EtapesImprimees({ lignes }: { readonly lignes: readonly LigneBloc[] }) {
  return (
    <>
      {lignes.map((ligne) => (
        <div
          key={ligne.id}
          className={ligne.autre ? 'plan-etape plan-autre' : 'plan-etape'}
        >
          <span
            className={ligne.chargement ? 'plan-sens plan-charge' : 'plan-sens plan-livre'}
          >
            {ligne.chargement ? '▼' : '▲'}
          </span>
          <span className="plan-lieu">
            {ligne.site}
            {ligne.departement === null ? null : (
              <span className="plan-dep"> {ligne.departement}</span>
            )}
          </span>
          <span className="plan-heure">
            {ligne.autre ? `${ligne.jour.slice(8)}/${ligne.jour.slice(5, 7)} ` : ''}
            {ligne.heure ?? ''}
          </span>
          <span className={ligne.litige ? 'plan-marque plan-mlitige' : 'plan-marque'}>
            {ligne.litige ? '⚠' : ligne.fait ? '✓' : ''}
          </span>
          {/* LA CHARGE EST PORTÉE PAR L'ÉTAPE, pas par l'ordre — c'est ainsi
              que l'écran l'affiche (`LignesDuBloc`, EcranPlanning.tsx:234).
              La remonter au niveau de l'ordre aurait perdu les cas où elle
              change d'un arrêt à l'autre, et fait dire au papier autre chose
              qu'à l'écran. */}
          {ligne.marchandise === null ? null : (
            <span className="plan-charge-dite">{ligne.marchandise}</span>
          )}
        </div>
      ))}
    </>
  );
}

/**
 * Un ordre, dans une case ou dans la pile des ordres à affecter.
 *
 * UN SEUL COMPOSANT POUR LES DEUX, et non deux jumeaux : un ordre doit se lire
 * de la même façon qu'il soit sur un camion ou en attente. Deux composants
 * auraient divergé à la première correction faite d'un seul côté — c'est la
 * leçon déjà tirée au lot 119 sur la confirmation et l'ordre d'affrètement.
 *
 * LE STATUT EST ÉCRIT EN TOUTES LETTRES en plus de la couleur. L'imprimante de
 * Mohamed est en couleur — il l'a confirmé le 23/10/2026 — mais le mot ne coûte
 * rien et survit à une photocopie, à un fax et à un bac d'encre vide.
 */
function OrdreImprime({ bloc }: { readonly bloc: Bloc | CartePile }) {
  const tournee = 'tournee' in bloc ? bloc.tournee : null;
  const suiteAvant = 'suite_avant' in bloc ? bloc.suite_avant : false;
  const suiteApres = 'suite_apres' in bloc ? bloc.suite_apres : false;
  const reprisPar = 'repris_par' in bloc ? bloc.repris_par : null;
  const classes = ['plan-ot', `plan-${bloc.classe}`];
  if (bloc.litige) classes.push('plan-litige');
  if (suiteApres) classes.push('plan-suite-apres');
  return (
    <div className={classes.join(' ')}>
      <div className="plan-tete-ot">
        <span className="plan-numero">
          {suiteAvant ? '‹ ' : ''}
          {bloc.numero}
        </span>
        {tournee === null ? null : <span className="plan-tournee">{tournee}</span>}
        <span className="plan-statut">
          {statutDit(bloc.statut)}
          {bloc.litige ? ' · litige' : ''}
        </span>
      </div>
      <EtapesImprimees lignes={bloc.lignes} />
      {reprisPar === null ? null : (
        <div className="plan-relais">
          livré par <b>{reprisPar}</b>
        </div>
      )}
      <div className="plan-client">{bloc.client}</div>
    </div>
  );
}

/**
 * La légende des couleurs et des signes.
 *
 * ELLE N'EST PAS RÉPÉTÉE D'UNE PAGE À L'AUTRE, et ce n'est pas un oubli : la
 * tentative est racontée plus bas, à l'endroit où elle est posée.
 */
function Legende() {
  return (
    <div className="plan-legende">
      <span>
        <i className="plan-pastille plan-planifie" />
        planifié
      </span>
      <span>
        <i className="plan-pastille plan-encours" />
        en cours
      </span>
      <span>
        <i className="plan-pastille plan-livre" />
        livré
      </span>
      <span>
        <i className="plan-pastille plan-annule" />
        annulé
      </span>
      <span>
        <i className="plan-pastille plan-attente" />à affecter
      </span>
      <span>▼ chargement</span>
      <span>▲ livraison</span>
      <span>✓ étape faite</span>
      <span>⚠ litige</span>
      <span>— rien ce jour-là</span>
    </div>
  );
}

/**
 * LA FEUILLE SEULE, sans la barre d'outils ni le voile.
 *
 * Séparée pour la raison qui a servi à la confirmation et à l'ordre
 * d'affrètement : elle NE FAIT QUE DESSINER, donc elle se montre dans l'aperçu,
 * donc elle se vérifie sans base de données et sans impression d'essai.
 */
export function FeuillePlanning({
  semaine,
  vehicules,
  cases,
  groupes,
  indisponibilites,
  recherche,
  imprimeLe,
}: DonneesPlanningImprime) {
  const combien = groupes.reduce((t, g) => t + g.cartes.length, 0);
  const titre = semaineDite(semaine.du, semaine.au) ?? '';
  return (
    <div className="plan-document">
      <table className="plan-grille">
        {/*
          LES LARGEURS VIENNENT D'UN COLGROUP, ET PAS DES CELLULES.

          CONSTATÉ le 23/10/2026 en regardant la première page produite : la
          colonne du chauffeur sortait à 177 px alors qu'elle en demandait 128,
          et les six colonnes faisaient toutes exactement 1062 / 6 = 177 px.

          LA CAUSE : en largeurs imposées, les largeurs se lisent dans la
          PREMIÈRE RANGÉE du tableau. Ici la première rangée est le bandeau de
          titre, une cellule unique en `colSpan` : elle ne déclare aucune
          largeur de colonne, et le navigateur partage tout à égalité.

          Un `colgroup` est lu avant toute rangée. Les jours y ont gagné 13 px
          chacun, et le tableau 84 px — une page de moins.
        */}
        <colgroup>
          <col className="plan-col-camion" />
          {semaine.jours.map((jour) => (
            <col key={jour} />
          ))}
        </colgroup>

        {/*
          L'EN-TÊTE SE RÉPÈTE SUR CHAQUE PAGE, par `table-header-group`. Sans
          lui, la page 2 serait un tableau sans jours : on y lirait des ordres
          sans savoir lequel tombe le mardi.
        */}
        <thead>
          <tr className="plan-bandeau">
            <th colSpan={semaine.jours.length + 1}>
              <div className="plan-bandeau-dedans">
                <h1>Planning</h1>
                <span className="plan-sous">
                  {titre} · {vehicules.length} véhicule
                  {vehicules.length > 1 ? 's' : ''}
                </span>
                <span className="plan-droite">imprimé le {imprimeLe}</span>
              </div>
              {recherche.trim() === '' ? null : (
                <p className="plan-filtre">
                  Filtré sur : « {recherche.trim()} » — ce document ne porte que les
                  ordres qui correspondent à cette recherche.
                </p>
              )}
            </th>
          </tr>
          <tr className="plan-colonnes">
            <th>Chauffeur</th>
            {semaine.jours.map((jour) => (
              <th key={jour} className={finDeSemaine(jour) ? 'plan-we' : undefined}>
                {colonneDite(jour)}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {vehicules.map((vehicule) => {
            const tete = teteDeLigne(vehicule);
            return (
              <tr key={vehicule.id}>
                <td className="plan-camion">
                  <div className={tete.sansChauffeur ? 'plan-nom plan-sans' : 'plan-nom'}>
                    {tete.principal}
                  </div>
                  <div className="plan-materiel">{tete.materiel}</div>
                </td>
                {semaine.jours.map((jour) => {
                  const blocs = cases.get(`${vehicule.id}|${jour}`) ?? [];
                  const empeche = empechementDe(vehicule, jour, indisponibilites);
                  const vide = blocs.length === 0;
                  return (
                    <td
                      key={jour}
                      className={`plan-case${finDeSemaine(jour) ? ' plan-we' : ''}${
                        empeche === null ? '' : ' plan-empechee'
                      }${vide && empeche === null ? ' plan-rien' : ''}`}
                    >
                      {empeche === null ? null : (
                        <div className="plan-empeche">
                          {empeche.motif}
                          <span>
                            {empeche.nom}
                            {empeche.note === null ? '' : ` · ${empeche.note}`}
                          </span>
                        </div>
                      )}
                      {vide && empeche === null ? (
                        <span className="plan-mot-rien">—</span>
                      ) : (
                        blocs.map((bloc) => (
                          <OrdreImprime key={`${bloc.ot_id}|${bloc.jour}`} bloc={bloc} />
                        ))
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>

      {/*
        LA LÉGENDE EST HORS DU TABLEAU, et une seule fois.

        ELLE ÉTAIT DANS UN `tfoot` EN `table-footer-group`, pour se répéter en
        bas de chaque page comme le fait l'en-tête. MESURÉ le 23/10/2026 sur
        l'aperçu, et c'est pour cela que ce commentaire existe :

          - le pied répété AJOUTAIT UNE PAGE ENTIÈRE au document dès que le
            tableau tenait sur une seule : trois pages au lieu de deux, dont
            une ne portant que l'en-tête, la mention du filtre et la légende,
            sans un seul chauffeur ;
          - et il ne se répétait même pas partout : sur un planning de trois
            pages, la légende ne sortait que sur la dernière.

        Le pied coûtait donc une page blanche pour un service qu'il ne rendait
        pas. L'EN-TÊTE, LUI, SE RÉPÈTE BIEN — c'est lui qui compte : sans les
        jours en haut de la page 2, on lirait des ordres sans savoir quel jour
        ils tombent. Une légende ne se lit qu'une fois.
      */}
      <Legende />

      {/*
        LES ORDRES À AFFECTER, en dernière page.

        ILS NE SONT PAS DANS LE TABLEAU, et ne peuvent pas l'être : ils ne sont
        sur aucun camion, donc sur aucune ligne. Ils commencent une page à eux,
        parce que c'est une liste de travail qu'on détache pour la traiter.
      */}
      {combien === 0 ? null : (
        <section className="plan-a-affecter">
          <div className="plan-bandeau-dedans plan-bandeau-pile">
            <h1>Ordres à affecter</h1>
            <span className="plan-sous">
              {titre} · {combien} ordre{combien > 1 ? 's' : ''} sur aucun camion
            </span>
            <span className="plan-droite">imprimé le {imprimeLe}</span>
          </div>
          <div className="plan-colonnes-pile">
            {groupes.map((groupe) => (
              <div key={groupe.titre} className="plan-groupe">
                <p>{groupe.titre}</p>
                {groupe.cartes.map((carte) => (
                  <OrdreImprime key={carte.ot_id} bloc={carte} />
                ))}
              </div>
            ))}
          </div>
          <Legende />
        </section>
      )}
    </div>
  );
}

/**
 * LE VOILE, sorti dans `document.body` par un portail.
 *
 * MÊME RAISON QU'À LA CONFIRMATION : à l'impression, tout ce qui n'est pas ce
 * portail passe en `display: none`. Laissée dans l'écran du planning, la
 * feuille aurait disparu avec lui.
 *
 * LA RACINE PORTE DEUX CLASSES. `conf-racine` la fait survivre à l'impression,
 * par la règle du lot 121 qui ne garde en flux que ce portail. `plan-racine`
 * ajoute le PAYSAGE : la page nommée du planning, puisque le reste de
 * l'application s'imprime en portrait.
 */
function VoilePlanning({ children }: { readonly children: ReactNode }) {
  return createPortal(
    <div className="application conf-racine plan-racine">
      <div className="conf-voile plan-voile">{children}</div>
    </div>,
    document.body,
  );
}

export interface ProprietesPlanningImprime extends DonneesPlanningImprime {
  readonly quandFermer: () => void;
}

/** L'aperçu complet : la barre d'écran, puis la feuille. */
export function PlanningImprime({
  quandFermer,
  ...donnees
}: ProprietesPlanningImprime) {
  return (
    <VoilePlanning>
      <div className="conf-outils">
        <b>Planning — {semaineDite(donnees.semaine.du, donnees.semaine.au) ?? ''}</b>
        <button className="bouton" type="button" onClick={quandFermer}>
          Fermer
        </button>
        <button
          className="bouton conf-primaire"
          type="button"
          onClick={() => {
            window.print();
          }}
        >
          Imprimer / Enregistrer en PDF
        </button>
        <span className="conf-aide">
          Format paysage, A4. Le PDF est produit par le navigateur : choisissez
          « Enregistrer au format PDF ».
        </span>
      </div>

      <div className="conf-cadre plan-cadre">
        <FeuillePlanning {...donnees} />
      </div>
    </VoilePlanning>
  );
}
